import React, {useState, useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import axios from 'axios';
import CustomSelect from '../common/CustomSelect';
import './ConnectionsList.css';
import {toast, ToastContainer} from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const initState = {
    id: 0,
    conn_descr: '',
    exchange: '',
    api_key: '',
    api_secret: '',
    api_passphrase: '',
    market: 'futures',
    align_exist_hedge_pos: 1,
    align_hedge_after_open: 1
};

const ConnectionsList = () => {
    const {t} = useTranslation();
    const [connections, setConnections] = useState([]);
    const [filteredConnections, setFilteredConnections] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showAddForm, setShowAddForm] = useState(false);
    const [editingConnection, setEditingConnection] = useState(null);
    const [formErrors, setFormErrors] = useState({});
    const [showFilters, setShowFilters] = useState(false);
    const [filters, setFilters] = useState({
        name: '',
        exchange: '',
        market: 'all',
        status: 'all'
    });
    const navigate = useNavigate();

    // Форма для нового подключения
    const [formData, setFormData] = useState(initState);

    // Загрузка списка подключений
    const fetchConnections = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                navigate('/login');
                return;
            }

            // Проверка срока действия токена
            const tokenExpiry = localStorage.getItem('tokenExpiry');
            if (tokenExpiry && new Date().getTime() > parseInt(tokenExpiry)) {
                localStorage.removeItem('token');
                localStorage.removeItem('tokenExpiry');
                toast.error(t('connections.sessionExpired'));
                navigate('/login');
                return;
            }

            const response = await axios.get('/api/connections/', {
                headers: {Authorization: `Bearer ${token}`}
            });
            setConnections(response.data);
            setError('');
        } catch (err) {
            setError(t('connections.loadError'));
            if (err.response?.status === 401) {
                localStorage.removeItem('token');
                localStorage.removeItem('tokenExpiry');
                toast.error(t('connections.loginRequired'));
                navigate('/login');
            } else {
                toast.error(err.response?.data?.detail || t('connections.loadError'));
            }
        } finally {
            setLoading(false);
        }
    };

    // Функция для применения фильтров
    const applyFilters = () => {
        if (!connections.length) return;

        let result = [...connections];

        // Фильтрация по названию
        if (filters.name) {
            result = result.filter(connection =>
                connection.conn_descr.toLowerCase().includes(filters.name.toLowerCase()) || connection.id.toString().includes(filters.name)
            );
        }

        // Фильтрация по бирже
        if (filters.exchange) {
            result = result.filter(connection =>
                connection.exchange.toLowerCase().includes(filters.exchange.toLowerCase())
            );
        }

        // Фильтрация по типу маркета
        if (filters.market !== 'all') {
            result = result.filter(connection => connection.market === filters.market);
        }

        // Фильтрация по статусу
        if (filters.status !== 'all') {
            const activeStatus = filters.status === 'active' ? 1 : 0;
            result = result.filter(connection => connection.active === activeStatus);
        }

        setFilteredConnections(result);
    };

    // Обработчик изменения фильтров
    const handleFilterChange = (e) => {
        const {name, value} = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Сброс фильтров
    const resetFilters = () => {
        setFilters({
            name: '',
            exchange: '',
            market: 'all',
            status: 'all'
        });
    };

    // Загрузка при монтировании компонента
    useEffect(() => {
        fetchConnections().then(() => {
        });

        // Автоматичний вихід при неактивності видалений - сесія тепер триває 30 днів
    }, [navigate]);

    // Применение фильтров при изменении данных или фильтров
    useEffect(() => {
        applyFilters();
    }, [connections, filters]);

    // Handle ESC key to close forms and filters
    useEffect(() => {
        const handleEscKey = (event) => {
            if (event.key === 'Escape') {
                // Close form if open (same as Back button)
                if (showAddForm || editingConnection) {
                    setShowAddForm(false);
                    setEditingConnection(null);
                    setFormData(initState);
                    setFormErrors({});
                    return;
                }
                // Close filters if open
                if (showFilters) {
                    setShowFilters(false);
                }
            }
        };

        document.addEventListener('keydown', handleEscKey);
        return () => document.removeEventListener('keydown', handleEscKey);
    }, [showAddForm, editingConnection, showFilters]);

    // Валидация формы
    const validateForm = () => {
        const errors = {};

        if (!formData.conn_descr.trim()) {
            errors.conn_descr = t('connections.validation.nameRequired');
        }

        if (!editingConnection && !formData.exchange.trim()) {
            errors.exchange = t('connections.validation.exchangeRequired');
        }

        if (!formData.api_key.trim()) {
            errors.api_key = t('connections.validation.apiKeyRequired');
        } else if (!editingConnection && formData.api_key.trim().length < 8) {
            errors.api_key = t('connections.validation.apiKeyMinLength');
        }

        if (!editingConnection && !formData.api_secret.trim()) {
            errors.api_secret = t('connections.validation.apiSecretRequired');
        } else if (!editingConnection && formData.api_secret.trim() && formData.api_secret.trim().length < 8) {
            errors.api_secret = t('connections.validation.apiSecretMinLength');
        }

        // Validation for api_passphrase (required for OKX only when creating new connection)
        if (!editingConnection && formData.exchange === 'okx' && !formData.api_passphrase.trim()) {
            errors.api_passphrase = t('connections.validation.apiPassphraseRequired');
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Обработка изменения полей формы
    const handleInputChange = (e) => {
        const {name, value} = e.target;
        setFormData({
            ...formData,
            [name]: value
        });

        // Очистка ошибки при вводе
        if (formErrors[name]) {
            setFormErrors({
                ...formErrors,
                [name]: ''
            });
        }
    };

    // Добавление нового подключения
    const handleAddConnection = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        try {
            const token = localStorage.getItem('token');
            await axios.post('/api/connections/', formData, {
                headers: {Authorization: `Bearer ${token}`}
            });

            // Очистка формы и обновление списка
            setFormData(initState);
            setShowAddForm(false);
            toast.success(t('connections.connectionAdded'));
            await fetchConnections();
        } catch (err) {
            toast.error(err.response?.data?.detail || t('connections.addError'));
        }
    };

    // Редактирование подключения
    const handleEditConnection = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        try {
            const token = localStorage.getItem('token');
            await axios.put(`/api/connections/${editingConnection.id}`, formData, {
                headers: {Authorization: `Bearer ${token}`}
            });

            setEditingConnection(null);
            toast.success(t('connections.connectionUpdated'));
            await fetchConnections();
        } catch (err) {
            toast.error(err.response?.data?.detail || t('connections.updateError'));
        }
    };

    // Начало редактирования подключения
    const startEditing = (connection) => {
        setShowFilters(false);
        setEditingConnection(connection);
        setFormData({
            conn_descr: connection.conn_descr,
            exchange: connection.exchange,
            api_key: connection.api_key,
            api_secret: '',  // Не показываем секретный ключ
            api_passphrase: '',  // Не показываем секретную фразу
            market: connection.market,
            align_exist_hedge_pos: connection.align_exist_hedge_pos,
            align_hedge_after_open: connection.align_hedge_after_open
        });
        setShowAddForm(false);
        setFormErrors({});
    };

    // Переименование подключения
    // const handleRenameConnection = async (id, newName) => {
    //     if (!newName.trim()) {
    //         toast.error('Название подключения не может быть пустым');
    //         return;
    //     }
    //
    //     try {
    //         const token = localStorage.getItem('token');
    //         await axios.patch(`/api/connections/${id}/rename`, {conn_descr: newName}, {
    //             headers: {Authorization: `Bearer ${token}`}
    //         });
    //
    //         toast.success('Подключение успешно переименовано');
    //         fetchConnections();
    //     } catch (err) {
    //         toast.error(err.response?.data?.detail || 'Ошибка при переименовании подключения');
    //     }
    // };

    // Изменение активности подключения
    const handleToggleActive = async (id, currentActive) => {
        const newActive = currentActive === 1 ? 0 : 1;
        const confirmMessage = newActive === 1 ? t('connections.activateConfirm') : t('connections.deactivateConfirm');

        if (!window.confirm(confirmMessage)) {
            return;
        }

        try {
            const token = localStorage.getItem('token');
            await axios.patch(`/api/connections/${id}/activate/${newActive}`, {}, {
                headers: {Authorization: `Bearer ${token}`}
            });

            const successMessage = newActive === 1 ? t('connections.connectionActivated') : t('connections.connectionDeactivated');
            toast.success(successMessage);
            await fetchConnections();
        } catch (err) {
            toast.error(err.response?.data?.detail || t('connections.toggleError'));
        }
    };

    // Удаление подключения
    const handleDeleteConnection = async (id) => {
        if (!window.confirm(t('connections.deleteConfirm'))) {
            return;
        }

        try {
            const token = localStorage.getItem('token');
            await axios.delete(`/api/connections/${id}`, {
                headers: {Authorization: `Bearer ${token}`}
            });

            toast.success(t('connections.connectionDeleted'));
            await fetchConnections();
        } catch (err) {
            toast.error(err.response?.data?.detail || t('connections.deleteError'));
        }
    };

    // Маскирование API ключей
    // const maskApiKey = (key) => {
    //     if (!key) return '';
    //     if (key.length <= 8) return '********';
    //     return `${key.substring(0, 4)}******${key.substring(key.length - 4)}`;
    // };

    // Копирование API ключа в буфер обмена
    // const copyToClipboard = (text) => {
    //     navigator.clipboard.writeText(text)
    //         .then(() => {
    //             toast.info('Скопировано в буфер обмена');
    //         })
    //         .catch(err => {
    //             toast.error('Не удалось скопировать');
    //         });
    // };

    return (
        <div className="connections-container">
            <ToastContainer position="top-right" autoClose={3000}/>

            <div className="header-with-actions">
                <h2>{t('connections.title')}</h2>
                <div className="connections-actions">
                    {!(showAddForm || editingConnection) && (
                        <>
                            <button
                                className="add-button"
                                onClick={() => {
                                    setShowFilters(false);
                                    if (!editingConnection) setShowAddForm(!showAddForm);
                                    setEditingConnection(null);
                                    setFormData(initState);
                                    setFormErrors({});
                                }}
                            >
                                {t('connections.addConnection')}
                            </button>
                            <button
                                className="filter-button"
                                onClick={() => setShowFilters(!showFilters)}
                            >
                                {showFilters ? t('connections.hideFilters') : t('connections.showFilters')}
                            </button>
                        </>
                    )}
                </div>
            </div>

            {error && <div className="error-message">{error}</div>}

            {/* Фильтры для поиска подключений */}
            {showFilters && (
                <div className="filters-container">
                    <h3>{t('connections.filters')}</h3>
                    <div className="filters-form">
                        <div className="filter-group">
                            <label htmlFor="name">{t('connections.connectionName')}</label>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                value={filters.name}
                                onChange={handleFilterChange}
                                placeholder={t('connections.nameFilter')}
                            />
                        </div>

                        <div className="filter-group">
                            <label htmlFor="exchange">{t('connections.exchange')}</label>
                            <input
                                type="text"
                                id="exchange"
                                name="exchange"
                                value={filters.exchange}
                                onChange={handleFilterChange}
                                placeholder={t('connections.exchangeFilter')}
                            />
                        </div>

                        <div className="filter-group">
                            <label htmlFor="market">{t('connections.marketType')}</label>
                            <CustomSelect
                                id="market"
                                name="market"
                                value={filters.market}
                                onChange={handleFilterChange}
                                options={[
                                    { value: 'all', label: t('connections.all') },
                                    { value: 'futures', label: t('connections.marketFutures') },
                                    { value: 'spot', label: t('connections.marketSpot') }
                                ]}
                            />
                        </div>

                        <div className="filter-group">
                            <label htmlFor="status">{t('common.status')}</label>
                            <CustomSelect
                                id="status"
                                name="status"
                                value={filters.status}
                                onChange={handleFilterChange}
                                options={[
                                    { value: 'all', label: t('connections.all') },
                                    { value: 'active', label: t('connections.statusActive') },
                                    { value: 'inactive', label: t('connections.statusInactive') }
                                ]}
                            />
                        </div>
                    </div>

                    <div className="filters-actions">
                        <button
                            className="reset-filters-button"
                            onClick={resetFilters}
                        >
                            {t('connections.resetFilters')}
                        </button>
                    </div>
                </div>
            )}

            {/* Форма добавления/редактирования подключения */}
            {(showAddForm || editingConnection) && (
                <div className="connection-form-container">
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px'}}>
                        <h3>{editingConnection ? t('connections.editConnection') : t('connections.newConnection')}</h3>
                        <button
                            type="button"
                            className="cancel-button"
                            onClick={() => {
                                setShowAddForm(false);
                                setEditingConnection(null);
                                setFormErrors({});
                                setFormData(initState);
                            }}
                        >
                            {t('common.back')}
                        </button>
                    </div>
                    <form onSubmit={editingConnection ? handleEditConnection : handleAddConnection}>
                        <div className={`form-group ${formErrors.conn_descr ? 'has-error' : ''}`}>
                            <label htmlFor="conn_descr">
                                {t('connections.connectionName')}
                                {/*<span*/}
                                {/*    className="tooltip-icon"*/}
                                {/*    title="Введите уникальное название для идентификации подключения"*/}
                                {/*>?</span>*/}
                            </label>
                            <input
                                type="text"
                                id="conn_descr"
                                name="conn_descr"
                                value={formData.conn_descr}
                                onChange={handleInputChange}
                                placeholder={t('connections.connectionNamePlaceholder')}
                            />
                            {formErrors.conn_descr && <div className="error-text">{formErrors.conn_descr}</div>}
                        </div>

                        {!editingConnection && (
                            <>
                                <div className={`form-group ${formErrors.exchange ? 'has-error' : ''}`}>
                                    <label htmlFor="exchange">
                                        {t('connections.exchange')}
                                        {/*<span className="tooltip-icon" title="Укажите название биржи">?</span>*/}
                                    </label>
                                    <CustomSelect
                                        id="exchange"
                                        name="exchange"
                                        value={formData.exchange}
                                        onChange={handleInputChange}
                                        options={[
                                            { value: 'binance', label: 'Binance' },
                                            { value: 'bybit', label: 'ByBit' },
                                            { value: 'okx', label: 'OKX' }
                                        ]}
                                    />
                                    {formErrors.exchange && <div className="error-text">{formErrors.exchange}</div>}
                                </div>

                                <div className={`form-group ${formErrors.api_key ? 'has-error' : ''}`}>
                                    <label htmlFor="api_key">
                                        {t('connections.apiKey')}
                                        {/*<span className="tooltip-icon"*/}
                                        {/*      title="Введите публичный API ключ от вашего аккаунта на бирже">?</span>*/}
                                    </label>
                                    <input
                                        type="text"
                                        id="api_key"
                                        name="api_key"
                                        value={formData.api_key}
                                        onChange={handleInputChange}
                                        placeholder={t('connections.apiKeyPlaceholder')}
                                    />
                                    {formErrors.api_key && <div className="error-text">{formErrors.api_key}</div>}
                                </div>

                                <div className={`form-group ${formErrors.api_secret ? 'has-error' : ''}`}>
                                    <label htmlFor="api_secret">
                                        {t('connections.apiSecret')}
                                        {/*<span className="tooltip-icon"*/}
                                        {/*      title="Введите секретный API ключ. Никогда не передавайте его третьим лицам!">?</span>*/}
                                    </label>
                                    <input
                                        type="password"
                                        id="api_secret"
                                        name="api_secret"
                                        value={formData.api_secret}
                                        onChange={handleInputChange}
                                        placeholder={editingConnection ? t('connections.apiSecretPlaceholderEdit') : t('connections.apiSecretPlaceholder')}
                                    />
                                    {formErrors.api_secret && <div className="error-text">{formErrors.api_secret}</div>}
                                </div>

                                {formData.exchange === 'okx' && (
                                    <div className={`form-group ${formErrors.api_passphrase ? 'has-error' : ''}`}>
                                        <label htmlFor="api_passphrase">
                                            {t('connections.apiPassphrase')}
                                            {/*<span className="tooltip-icon"*/}
                                            {/*      title="Для OKX требуется дополнительная секретная фраза">?</span>*/}
                                        </label>
                                        <input
                                            type="password"
                                            id="api_passphrase"
                                            name="api_passphrase"
                                            value={formData.api_passphrase}
                                            onChange={handleInputChange}
                                            placeholder={editingConnection ? t('connections.apiPassphraseEditPlaceholder') : t('connections.apiPassphrasePlaceholder')}
                                        />
                                        {formErrors.api_passphrase && <div className="error-text">{formErrors.api_passphrase}</div>}
                                    </div>
                                )}

                                <div className="form-group">
                                    <label htmlFor="market">
                                        {t('connections.market')}
                                        {/*<span className="tooltip-icon"*/}
                                        {/*      title="Выберите тип рынка для этого подключения">?</span>*/}
                                    </label>
                                    <CustomSelect
                                        id="market"
                                        name="market"
                                        value={formData.market}
                                        onChange={handleInputChange}
                                        options={[
                                            { value: 'futures', label: t('connections.marketFutures') },
                                            { value: 'spot', label: t('connections.marketSpot') }
                                        ]}
                                    />
                                </div>
                            </>
                        )}

                        <div className="form-group">
                            <label htmlFor="align_exist_hedge_pos">
                                {t('connections.alignExistHedgePos')}
                                {/*<span className="tooltip-icon"*/}
                                {/*      title="Автоматически выравнивать позицию хеджа при изменении основной позиции">?</span>*/}
                            </label>
                            <CustomSelect
                                id="align_exist_hedge_pos"
                                name="align_exist_hedge_pos"
                                value={formData.align_exist_hedge_pos}
                                onChange={handleInputChange}
                                options={[
                                    { value: 1, label: t('common.yes') },
                                    { value: 0, label: t('common.no') }
                                ]}
                            />

                            <label htmlFor="align_hedge_after_open">
                                {t('connections.alignHedgeAfterOpen')}
                                {/*<span className="tooltip-icon"*/}
                                {/*      title="Автоматически выравнивать позицию хеджа при изменении основной позиции">?</span>*/}
                            </label>
                            <CustomSelect
                                id="align_hedge_after_open"
                                name="align_hedge_after_open"
                                value={formData.align_hedge_after_open}
                                onChange={handleInputChange}
                                options={[
                                    { value: 1, label: t('common.yes') },
                                    { value: 0, label: t('common.no') }
                                ]}
                            />
                        </div>

                        <div className="form-actions">
                            <button type="submit" className="submit-button">
                                {editingConnection ? t('connections.saveChanges') : t('connections.addConnection')}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* Список подключений */}
            {loading ? (
                <div className="loading">
                    <div className="spinner"></div>
                    <span>{t('common.loading')}</span>
                </div>
            ) : connections.length === 0 ? (
                <div className="no-data">
                    <p>{t('connections.noConnections')}</p>
                    <p className="no-data-hint">{t('connections.noConnectionsHint')}</p>
                </div>
            ) : filteredConnections.length === 0 ? (
                <div className="no-data">
                    <p>{t('connections.noMatchingConnections')}</p>
                </div>
            ) : (
                !(showAddForm || editingConnection) && (
                    <div className="connections-list">
                        {filteredConnections.map(connection => (
                            <div
                                key={connection.id}
                                className={`connection-card ${connection.active === 1 ? 'active' : 'inactive'} ${connection.market}-market`}
                            >
                                <div className="connection-header">
                                    <div className="connection-title">
                                        <h3>ID: {connection.id} | {connection.conn_descr}</h3>
                                        <div className="connection-badges">
                                            <span
                                                className="connection-badge exchange-badge">{t('connections.exchangeLabel')}: {connection.exchange}
                                            </span>
                                            <span className="connection-badge market-badge">
                                                {connection.market === 'futures' ? t('connections.marketFutures') :
                                                    connection.market === 'spot' ? t('connections.marketSpot') : t('connections.marketFixedPrice')}
                                            </span>
                                        </div>
                                    </div>
                                    <div
                                        className={`connection-status ${connection.active === 1 ? 'status-active' : 'status-inactive'}`}>
                                        {connection.active === 1 ? '🟢' : '🔴'}
                                    </div>
                                </div>

                                <div className="connection-actions">
                                    <button
                                        className="edit-button"
                                        onClick={() => startEditing(connection)}
                                        title={t('connections.editConnection')}
                                    >
                                        <i className="fas fa-edit"></i> {t('connections.edit')}
                                    </button>
                                    <button
                                        className={`toggle-button ${connection.active === 1 ? 'deactivate' : 'activate'}`}
                                        onClick={() => handleToggleActive(connection.id, connection.active)}
                                        title={connection.active === 1 ? t('connections.deactivate') : t('connections.activate')}
                                    >
                                        <i className={`fas ${connection.active === 1 ? 'fa-toggle-off' : 'fa-toggle-on'}`}></i>
                                        {connection.active === 1 ? t('connections.deactivate') : t('connections.activate')}
                                    </button>

                                    <button
                                        className="delete-button"
                                        onClick={() => handleDeleteConnection(connection.id)}
                                        title={t('connections.delete')}
                                    >
                                        <i className="fas fa-trash"></i> {t('connections.delete')}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )
            )}
        </div>
    );
};

export default ConnectionsList;
